import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { toJS } from 'mobx';
import get from 'lodash/get';
import omit from 'lodash/omit';
import uniq from 'lodash/uniq';
import Column from '../../../../core/layout/Column';
import ColumnSeparator from '../../../../core/layout/ColumnSeparator';
import ErrorText from '../../../../core/layout/ErrorText';
import Row from '../../../../core/layout/Row';
import Separator from '../../../../core/layout/Separator';
import Button from '../../../../core/form/Button';
import Table from '../../../../core/form/Table';
import TextInput from '../../../../core/form/TextInput';
import TranslationInputContainer from '../inputs/TranslationInputContainer';
import SlicesInputContainer from '../inputs/SlicesInputContainer';
import Audios from '../inputs/Audios';
import SpellCheckService from '../../services/SpellCheckService';

const createId = () => `${new Date().toISOString()}-${Math.random().toString(36).slice(2)}`;

const splitText = (text) => text.match(/\S+/g) || [];

const normalizeAnswerText = (text) =>
  (text || '')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[!.?,]+$/g, '');

const sanitizeDictionary = (dictionary) =>
  Object.keys(dictionary || {}).reduce((result, key) => {
    const value = get(dictionary, key, '').trim();
    return value ? { ...result, [key]: value } : result;
  }, {});

const hasEmptyDictionaryValue = (dictionary) =>
  Object.keys(dictionary || {}).some((key) => !get(dictionary, key, '').trim());

const buildIndexesKey = (indexes) => indexes.join('-');

const groupSlices = (slices) => {
  const orderedSlices = [...slices].sort((a, b) => a.index - b.index);
  return orderedSlices.reduce((groups, slice) => {
    const previousGroup = groups[groups.length - 1];
    const previousSlice = previousGroup && previousGroup[previousGroup.length - 1];
    if (previousSlice && previousSlice.linkTo === slice.index) {
      previousGroup.push(slice);
      return groups;
    }
    groups.push([slice]);
    return groups;
  }, []);
};

const buildGapFromSlices = (sliceGroup, existingGap) => {
  const indexes = sliceGroup.map((slice) => slice.index);
  const text = sliceGroup.map((slice) => slice.text || slice.word).join(' ');
  return {
    id: get(existingGap, 'id', createId()),
    indexes,
    text,
    acceptedAnswers: get(existingGap, 'acceptedAnswers.length')
      ? existingGap.acceptedAnswers
      : [
          {
            id: createId(),
            text,
          },
        ],
  };
};

const gapsToSlices = (gaps) =>
  gaps.reduce((result, gap) => {
    const words = splitText(gap.text);
    return [
      ...result,
      ...gap.indexes.map((index, wordIndex) => ({
        id: `${gap.id}-${index}`,
        index,
        text: words[wordIndex] || gap.text,
        correct: true,
        ...(wordIndex < gap.indexes.length - 1 && { linkTo: gap.indexes[wordIndex + 1] }),
      })),
    ];
  }, []);

const gapsToAnswers = (gaps) =>
  gaps.reduce((result, gap) => {
    const words = splitText(gap.text);
    return [
      ...result,
      ...gap.indexes.map((index, wordIndex) => ({
        id: `${gap.id}-${index}`,
        index,
        text: words[wordIndex] || gap.text,
        correct: true,
        ...(wordIndex < gap.indexes.length - 1 && { linkTo: gap.indexes[wordIndex + 1] }),
      })),
    ];
  }, []);

const hasConsecutiveUnlinkedGaps = (gaps) => {
  const orderedGaps = [...gaps].sort((a, b) => a.indexes[0] - b.indexes[0]);
  return orderedGaps.some((gap, index) => {
    const nextGap = orderedGaps[index + 1];
    return nextGap && gap.indexes[gap.indexes.length - 1] + 1 === nextGap.indexes[0];
  });
};

class GapFillDictationItemForm extends Component {
  mainSpellCheckService = new SpellCheckService();
  answerSpellCheckService = new SpellCheckService();

  state = {
    selectedGapId: null,
    answerDraft: '',
    answerDraftId: null,
    answerDictionary: {},
  };

  componentDidMount() {
    const gaps = get(this.props.values, 'gaps', []);
    if (gaps.length && !this.state.selectedGapId) {
      this.setState({ selectedGapId: gaps[0].id });
    }
  }

  componentDidUpdate(prevProps) {
    const previousGaps = get(prevProps.values, 'gaps', []);
    const gaps = get(this.props.values, 'gaps', []);
    if (previousGaps !== gaps && gaps.length && !gaps.find((gap) => gap.id === this.state.selectedGapId)) {
      this.setState({ selectedGapId: gaps[0].id });
    }
    if (previousGaps.length && !gaps.length && this.state.selectedGapId) {
      this.setState({ selectedGapId: null });
    }
  }

  handleTextChange = (value) => {
    this.props.onChange('text', value);
    this.props.onChange('gaps', []);
    this.props.onChange('answers', []);
    this.props.onChange('indexesToRemove', []);
    this.props.onChange('spellCheckStatus', '');
    this.props.onChange('spellCheckedText', null);
    this.setState({
      selectedGapId: null,
      answerDraft: '',
      answerDraftId: null,
      answerDictionary: {},
    });
    this.mainSpellCheckService.status = '';
    this.mainSpellCheckService.words = [];
    this.answerSpellCheckService.status = '';
    this.answerSpellCheckService.words = [];
  };

  handleMainSpellCheck = async () => {
    await this.mainSpellCheckService.handleSubmit(
      get(this.props.values, 'text', ''),
      get(this.props.values, 'speechRecognitionDictionary', undefined)
    );
    this.props.onChange('spellCheckStatus', this.mainSpellCheckService.status);
    if (this.mainSpellCheckService.status === 'OK') {
      this.props.onChange('spellCheckedText', get(this.props.values, 'text', ''));
    }
  };

  handleAnswerSpellCheck = async () => {
    await this.answerSpellCheckService.handleSubmit(this.state.answerDraft, this.state.answerDictionary);
  };

  handleSlicesChange = (textSlices) => {
    const currentGaps = get(this.props.values, 'gaps', []);
    const nextGaps = groupSlices(textSlices).map((sliceGroup) => {
      const indexes = sliceGroup.map((slice) => slice.index);
      const existingGap = currentGaps.find((gap) => buildIndexesKey(gap.indexes) === buildIndexesKey(indexes));
      return buildGapFromSlices(sliceGroup, existingGap);
    });
    this.props.onChange('gaps', nextGaps);
    this.props.onChange('answers', gapsToAnswers(nextGaps));
    this.props.onChange('indexesToRemove', gapsToSlices(nextGaps));
    this.setState({ selectedGapId: get(nextGaps, '0.id', null) });
  };

  handleSelectGap = (gapId) => {
    this.setState({
      selectedGapId: gapId,
      answerDraft: '',
      answerDraftId: null,
      answerDictionary: {},
    });
    this.answerSpellCheckService.status = '';
    this.answerSpellCheckService.words = [];
  };

  handleAnswerDraftChange = (value) => {
    this.setState({ answerDraft: value });
    this.answerSpellCheckService.status = '';
    this.answerSpellCheckService.words = [];
  };

  handleAnswerDictionaryChange = (word, value) => {
    this.setState({
      answerDictionary: {
        ...this.state.answerDictionary,
        [word.replace(/[.]/g, '_')]: value,
      },
    });
    this.answerSpellCheckService.status = '';
  };

  handleMainDictionaryChange = (word, value) => {
    this.props.onChange('speechRecognitionDictionary', {
      ...get(this.props.values, 'speechRecognitionDictionary', {}),
      [word.replace(/[.]/g, '_')]: value,
    });
    this.props.onChange('spellCheckStatus', '');
    this.props.onChange('spellCheckedText', null);
    this.mainSpellCheckService.status = '';
  };

  handleRemoveMainDictionaryWord = (word) => {
    this.props.onChange(
      'speechRecognitionDictionary',
      omit(get(this.props.values, 'speechRecognitionDictionary', {}), [word.replace(/[.]/g, '_')])
    );
    this.props.onChange('spellCheckStatus', '');
    this.props.onChange('spellCheckedText', null);
    this.mainSpellCheckService.status = '';
  };

  handleRemoveAnswerDictionaryWord = (word) => {
    this.setState({
      answerDictionary: omit(this.state.answerDictionary, [word.replace(/[.]/g, '_')]),
    });
    this.answerSpellCheckService.status = '';
  };

  handleSubmitAnswer = () => {
    const gaps = get(this.props.values, 'gaps', []);
    const selectedGap = gaps.find((gap) => gap.id === this.state.selectedGapId);
    const answerText = normalizeAnswerText(this.state.answerDraft);
    if (!selectedGap || !answerText || this.answerSpellCheckService.status !== 'OK') {
      return;
    }
    const duplicatedAnswer = selectedGap.acceptedAnswers.find(
      (answer) =>
        normalizeAnswerText(answer.text) === answerText &&
        (!this.state.answerDraftId || answer.id !== this.state.answerDraftId)
    );
    if (duplicatedAnswer) {
      return;
    }
    const nextAcceptedAnswers = this.state.answerDraftId
      ? selectedGap.acceptedAnswers.map((answer) => {
          if (answer.id !== this.state.answerDraftId) {
            return answer;
          }
          const answerDictionary = sanitizeDictionary(this.state.answerDictionary);
          const updatedAnswer = {
            ...answer,
            text: this.state.answerDraft.trim(),
          };
          if (Object.keys(answerDictionary).length) {
            updatedAnswer.speechRecognitionDictionary = answerDictionary;
          } else {
            delete updatedAnswer.speechRecognitionDictionary;
          }
          return updatedAnswer;
        })
      : [
          ...selectedGap.acceptedAnswers,
          {
            id: createId(),
            text: this.state.answerDraft.trim(),
            ...(Object.keys(sanitizeDictionary(this.state.answerDictionary)).length && {
              speechRecognitionDictionary: sanitizeDictionary(this.state.answerDictionary),
            }),
          },
        ];
    const nextGaps = gaps.map((gap) =>
      gap.id === selectedGap.id
        ? {
            ...gap,
            acceptedAnswers: nextAcceptedAnswers,
          }
        : gap
    );
    this.props.onChange('gaps', nextGaps);
    this.setState({ answerDraft: '', answerDraftId: null, answerDictionary: {} });
    this.answerSpellCheckService.status = '';
    this.answerSpellCheckService.words = [];
  };

  handleEditAnswer = (gapId, answerId) => {
    const gap = get(this.props.values, 'gaps', []).find((candidate) => candidate.id === gapId);
    const answer = get(gap, 'acceptedAnswers', []).find((candidate) => candidate.id === answerId);
    if (!answer) {
      return;
    }
    this.setState({
      selectedGapId: gapId,
      answerDraft: answer.text,
      answerDraftId: answer.id,
      answerDictionary: answer.speechRecognitionDictionary || {},
    });
    this.answerSpellCheckService.status = '';
    this.answerSpellCheckService.words = [];
  };

  handleDeleteAnswer = (gapId, answerId) => {
    const gaps = get(this.props.values, 'gaps', []);
    const nextGaps = gaps.map((gap) =>
      gap.id === gapId
        ? {
            ...gap,
            acceptedAnswers: gap.acceptedAnswers.filter((answer) => answer.id !== answerId),
          }
        : gap
    );
    this.props.onChange('gaps', nextGaps);
  };

  renderSpellCheckFeedback(service, dictionary, onDictionaryChange, onRemoveWord) {
    const wrongWords = toJS(service.words);
    const dictionaryWords = uniq([...wrongWords, ...Object.keys(dictionary || {})]);
    const dictionaryHasEmptyValues = hasEmptyDictionaryValue(dictionary);
    return (
      <div style={{ display: 'block', marginTop: 12 }}>
        {service.status && (
          <div
            style={{
              color: service.status === 'OK' ? 'green' : 'red',
              marginBottom: dictionaryWords.length ? 10 : 0,
            }}
          >
            {service.status === 'OK' && 'Great! no error found.'}
            {service.status === 'ERROR' && 'Check the fields below.'}
          </div>
        )}
        {(service.status === 'ERROR' || !!Object.keys(dictionary || {}).length) && dictionaryWords.length > 0 && (
          <div
            style={{
              border: '1px solid #e5e5e5',
              borderRadius: 4,
              padding: 12,
              maxWidth: 720,
            }}
          >
            <small style={{ display: 'block', marginBottom: 10 }}>Reference words for unrecognized terms</small>
            <div
              style={{
                display: 'grid',
                gap: 12,
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              }}
            >
              {dictionaryWords.map((word) => (
                <div
                  key={word.replace(/[.]/g, '_')}
                  style={{
                    alignItems: 'flex-end',
                    display: 'flex',
                    gap: 6,
                    minWidth: 0,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <TextInput
                      label={word}
                      disabled={this.props.submitting || this.props.disabled}
                      value={get(dictionary, word.replace(/[.]/g, '_'), '')}
                      onChange={(value) => onDictionaryChange(word, value)}
                    />
                  </div>
                  <Button size="sm" rounded icon="close" onClick={() => onRemoveWord(word)} />
                </div>
              ))}
            </div>
            {dictionaryHasEmptyValues && <ErrorText>Reference words cannot be empty.</ErrorText>}
          </div>
        )}
      </div>
    );
  }

  renderPreview() {
    const text = get(this.props.values, 'text', '');
    const gaps = get(this.props.values, 'gaps', []);
    const gapByIndex = gaps.reduce((result, gap) => ({ ...result, [gap.indexes[0]]: gap }), {});
    const skippedIndexes = gaps.reduce(
      (result, gap) => [...result, ...gap.indexes.filter((index) => index !== gap.indexes[0])],
      []
    );
    return splitText(text)
      .map((slice, index) => {
        if (gapByIndex[index]) {
          return '____';
        }
        if (skippedIndexes.includes(index)) {
          return null;
        }
        return slice;
      })
      .filter(Boolean)
      .join(' ');
  }

  render() {
    const { props } = this;
    const gaps = get(props.values, 'gaps', []);
    const selectedGap = gaps.find((gap) => gap.id === this.state.selectedGapId);
    const spellCheckApproved = get(props.values, 'spellCheckStatus', '') === 'OK';
    const mainDictionaryInvalid = hasEmptyDictionaryValue(get(props.values, 'speechRecognitionDictionary', {}));
    const answerDictionaryInvalid = hasEmptyDictionaryValue(this.state.answerDictionary);
    const duplicateAnswer =
      selectedGap &&
      this.state.answerDraft &&
      selectedGap.acceptedAnswers.find(
        (answer) =>
          normalizeAnswerText(answer.text) === normalizeAnswerText(this.state.answerDraft) &&
          (!this.state.answerDraftId || answer.id !== this.state.answerDraftId)
      );

    return (
      <div>
        <Row>
          <Column size={12}>
            <TextInput
              required
              label="Text"
              fieldType="textarea"
              disabled={props.submitting || props.disabled}
              value={get(props.values, 'text', '')}
              onChange={this.handleTextChange}
              errorText={get(props.errors, 'text', '')}
            />
          </Column>
        </Row>
        {!props.isTestItem && (
          <TranslationInputContainer
            onChange={props.onChange}
            submitting={props.submitting}
            values={props.values}
            errors={props.errors}
            disabled={props.disabled}
          />
        )}
        <Row>
          <Column size={12}>
            <Button
              disabled={props.submitting || props.disabled || !get(props.values, 'text', '') || mainDictionaryInvalid}
              label="Spell Check"
              loading={this.mainSpellCheckService.submit.fetching}
              onClick={this.handleMainSpellCheck}
            />
            {this.renderSpellCheckFeedback(
              this.mainSpellCheckService,
              get(props.values, 'speechRecognitionDictionary', {}),
              this.handleMainDictionaryChange,
              this.handleRemoveMainDictionaryWord
            )}
            {get(props.errors, 'spellCheckStatus', '') && (
              <ErrorText>{get(props.errors, 'spellCheckStatus', '')}</ErrorText>
            )}
          </Column>
        </Row>
        <Audios
          audioPath="audio"
          generatedAudioPath="generatedAudio"
          values={props.values}
          submitting={props.submitting}
          disabled={props.disabled || !spellCheckApproved}
          onChange={props.onChange}
          errors={props.errors}
        />
        <SlicesInputContainer
          onChange={this.handleSlicesChange}
          onSplit={splitText}
          text={get(props.values, 'text', '')}
          value={gapsToSlices(gaps)}
          errorText={get(props.errors, 'indexesToRemove', '')}
          label="Use X to select what words the student will have to write:"
          allowLinkSlices
          showPreview={false}
          disabled={props.disabled || !spellCheckApproved}
        />
        {gaps.length > 0 && (
          <div>
            <small>Text preview</small>
            <Separator size="xs" />
            {this.renderPreview()}
            {hasConsecutiveUnlinkedGaps(gaps) && (
              <ErrorText>Consecutive gaps must be linked into a single gap.</ErrorText>
            )}
          </div>
        )}
        <Separator size="sm" />
        {gaps.length > 0 && (
          <Row>
            <Column size={12}>
              <div>
                <small>Selected gap</small>
                <Separator size="xs" />
                {gaps.map((gap) => (
                  <span key={gap.id}>
                    <Button
                      type={gap.id === this.state.selectedGapId ? 'primary' : 'default'}
                      label={gap.text}
                      onClick={() => this.handleSelectGap(gap.id)}
                    />
                    <ColumnSeparator size="xs" />
                  </span>
                ))}
              </div>
            </Column>
            <Column size={12}>
              <div style={{ maxWidth: 720 }}>
                <TextInput
                  label="Add another possible answer"
                  disabled={props.disabled || !selectedGap}
                  value={this.state.answerDraft}
                  onChange={this.handleAnswerDraftChange}
                  errorText={duplicateAnswer ? 'Duplicated answer for this gap' : null}
                />
                <Button
                  disabled={props.disabled || !this.state.answerDraft || answerDictionaryInvalid}
                  label="Spell Check"
                  loading={this.answerSpellCheckService.submit.fetching}
                  onClick={this.handleAnswerSpellCheck}
                />
                {this.renderSpellCheckFeedback(
                  this.answerSpellCheckService,
                  this.state.answerDictionary,
                  this.handleAnswerDictionaryChange,
                  this.handleRemoveAnswerDictionaryWord
                )}
                <Separator size="xs" />
                <Button
                  icon="check"
                  type="primary"
                  label={this.state.answerDraftId ? 'Update' : 'Add'}
                  disabled={
                    props.disabled ||
                    !this.state.answerDraft ||
                    answerDictionaryInvalid ||
                    this.answerSpellCheckService.status !== 'OK' ||
                    !!duplicateAnswer
                  }
                  onClick={this.handleSubmitAnswer}
                />
                <ColumnSeparator size="xs" />
                <Button
                  icon="reload"
                  label="Discard"
                  onClick={() => {
                    this.setState({ answerDraft: '', answerDraftId: null, answerDictionary: {} });
                    this.answerSpellCheckService.status = '';
                    this.answerSpellCheckService.words = [];
                  }}
                />
              </div>
            </Column>
          </Row>
        )}
        <Separator size="sm" />
        <Table
          columns={[
            { label: 'Gap', path: 'gapText', width: '260px' },
            { label: 'Answer', path: 'text' },
            {
              label: 'Actions',
              width: '100px',
              render: (cell, row) =>
                !props.disabled && (
                  <div>
                    <Button icon="delete" onClick={() => this.handleDeleteAnswer(row.gapId, row.id)} />{' '}
                    <Button icon="edit" onClick={() => this.handleEditAnswer(row.gapId, row.id)} />
                  </div>
                ),
            },
          ]}
          rows={gaps.reduce(
            (rows, gap) => [
              ...rows,
              ...get(gap, 'acceptedAnswers', []).map((answer) => ({
                ...answer,
                gapId: gap.id,
                gapText: gap.text,
              })),
            ],
            []
          )}
        />
        {get(props.errors, 'gaps', '') && <ErrorText>{get(props.errors, 'gaps', '')}</ErrorText>}
      </div>
    );
  }
}

GapFillDictationItemForm.propTypes = {
  values: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.object,
  submitting: PropTypes.bool,
  disabled: PropTypes.bool,
  isTestItem: PropTypes.bool,
};

GapFillDictationItemForm.defaultProps = {
  values: {},
  errors: {},
  submitting: false,
  disabled: false,
  isTestItem: false,
};

export default observer(GapFillDictationItemForm);
